import React, { useState, useRef } from 'react';

const TextareaWithUploadStep = ({ question, value, onAnswer, onNext }) => {
  const [text, setText] = useState(value?.text || '');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(value?.imageData || null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file type
    const validTypes = ['image/jpeg'];
    if (!validTypes.includes(selectedFile.type)) {
      setError('Please upload an image file (JPEG)');
      return;
    }

    // Validate file size (max 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setError('');
    setUploading(true);
    setFile(selectedFile);

    // Convert to base64
    try {
      const reader = new FileReader();
      reader.readAsDataURL(selectedFile);
      reader.onload = () => {
        setPreview(reader.result);
        setUploading(false);
      };
      reader.onerror = () => {
        setError('Error processing file. Please try again.');
        setUploading(false);
      };
    } catch (err) {
      setError('Error processing file. Please try again.');
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleContinue = () => {
    if (!text.trim() && !preview) {
      setError('Please provide medication details or upload a photo');
      return;
    }

    onAnswer({
      text: text.trim(),
      imageData: preview,
      fileName: file?.name || null,
      uploadedAt: preview ? new Date().toISOString() : null
    });
    onNext();
  };

  return (
    <div data-testid={`step-${question.id}`}>
      {/* Question */}
      <h2 className="text-2xl md:text-3xl font-semibold text-center text-gray-900 mb-2">
        {question.question}
      </h2>

      {/* Subtitle with instructions */}
      {question.subtitle && (
        <div className="bg-blue-50 rounded-xl p-4 mb-6">
          <p className="text-sm text-blue-800 whitespace-pre-line">
            {question.subtitle}
          </p>
        </div>
      )}

      {/* Textarea */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Medication Details
        </label>
        <textarea
          value={text}
          onChange={(e) => { setText(e.target.value); setError(''); }}
          placeholder={question.placeholder || 'Enter your response here...'}
          rows={4}
          className="form-input resize-none"
          data-testid="medication-textarea"
        />
      </div>

      {/* Image Upload */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Upload Photo of Medication Label (Optional)
        </label>
        
        {!preview ? (
          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
            <div className="flex flex-col items-center justify-center py-4">
              {uploading ? (
                <>
                  <svg className="animate-spin w-8 h-8 text-[#2d8a5f] mb-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <p className="text-sm text-gray-500">Processing...</p>
                </>
              ) : (
                <>
                  <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm text-gray-500">Click to upload a photo</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleFileSelect}
              data-testid="image-upload-input"
            />
          </label>
        ) : (
          <div className="relative">
            <img
              src={preview}
              alt="Medication label"
              className="w-full h-32 object-contain bg-gray-100 rounded-xl"
            />
            <button
              onClick={handleRemoveImage}
              className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors"
              data-testid="remove-image"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {error && (
        <p className="text-red-500 text-sm text-center mb-4" data-testid="error-message">
          {error}
        </p>
      )}

      <button
        onClick={handleContinue}
        className="btn-primary"
        data-testid="continue-button"
      >
        Continue
      </button>
    </div>
  );
};

export default TextareaWithUploadStep;
